import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { isNonNullish } from 'remeda';
import { GET_DEFAULT_SIGNAL_BUSINESS_HOURS_QUERY } from 'src/graphql/entities/signal-business-hours/signal-business-hours.operations';
import {
  DefaultSignalBusinessHour,
  DefaultSignalBusinessHoursResponse,
  SignalUrgencyLevel,
  WeekDay,
} from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { Records } from 'src/graphql/types';
import { toEnum, toNumericEnum } from 'src/utils/enum';

export type GetSignalBusinessHoursQuery = {
  getDefaultSignalBusinessHours: Records<DefaultSignalBusinessHoursResponse>;
};

export const useDefaultSignalBusinessHours = () => {
  const { data, loading } = useQuery<GetSignalBusinessHoursQuery>(
    GET_DEFAULT_SIGNAL_BUSINESS_HOURS_QUERY,
  );

  const records = useMemo<DefaultSignalBusinessHour[]>(
    () =>
      (data?.getDefaultSignalBusinessHours.records || [])
        .map((item) => ({
          // We have misalignment in a way urgency levels defined on backend, there' two variations:
          // - with underscore divider, that is used for default values, ie `life_safety` (apps/backend/assets/lambdas/src/business-layer/domain/conversations/conversation-type.ts)
          // - and with dash divider, that is used in DB as Enum type, ie `life-safety`.
          // Here, we follow the same pattern to receive and adjust urgency level to DB defined type.
          urgency: toEnum(SignalUrgencyLevel, item.urgency.replace('_', '-'))!,
          signalBusinessHours: item.signalBusinessHours.map(
            ({ weekday, startHour, startMinute, endHour, endMinute }) => ({
              from: dayjs(`${startHour}:${startMinute}`, 'HH:mm'),
              to: dayjs(`${endHour}:${endMinute}`, 'HH:mm'),
              weekday: toNumericEnum(WeekDay, weekday)!,
            }),
          ),
        }))
        .filter(isNonNullish),
    [data],
  );

  return {
    defaultSignalBusinessHours: records,
    isLoading: loading,
  };
};
